import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import TagEditModal from '../components/TagEditModal';
import { useCollection } from '../hooks/useCollection';
import { useAuth } from '../context/AuthContext';
import useFeatureGate from '../hooks/useFeatureGate';
import { formatDate, formatGameName } from '../utils/format';

const PAGE_SIZE = 50;

/**
 * Format clip duration from seconds to mm:ss.
 */
function formatDuration(seconds) {
  if (seconds == null || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * CollectionPage — displays the user's saved clip collection.
 * Features: responsive grid, remove with confirmation, count/limit display,
 * Load More pagination, empty state.
 */
export default function CollectionPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    clips,
    loading,
    error,
    total,
    fetchCollection,
    fetchMore,
    removeFromCollection,
    addToCollection,
    updateClipTags,
    bulkUpdateTags,
  } = useCollection();

  const [removingId, setRemovingId] = useState(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [tagModalClip, setTagModalClip] = useState(null);
  const [tagModalTags, setTagModalTags] = useState([]);
  const [tagModalError, setTagModalError] = useState(null);
  const [tagSaving, setTagSaving] = useState(false);
  const [selectedClipIds, setSelectedClipIds] = useState(new Set());

  // Get collection limit and access from tier
  /* c8 ignore start */
  const collectionMaxClips = user?.tier?.features?.collection?.maxClips ?? 15;
  /* c8 ignore stop */
  const collectionEnabled = useFeatureGate('feature:collection') && user?.tier?.features?.collection?.enabled !== false;

  // Advanced filters feature gating
  const advancedFiltersGlobal = useFeatureGate('feature:advanced_filters');
  const advancedFiltersTier = user?.tier?.features?.collection?.advancedFilters ?? false;
  const showAdvancedFilters = advancedFiltersGlobal && advancedFiltersTier;

  // Tagging feature gating
  const taggingGlobal = useFeatureGate('feature:collection:tagging');
  const taggingTier = user?.tier?.features?.collection?.tagging ?? false;
  const showTagging = taggingGlobal && taggingTier;

  // Whether this tier enforces own-channel-only for collection additions
  const collectionOwnChannelOnly = user?.tier?.features?.collection?.ownChannelOnly ?? true;

  // Playback feature gating
  const playbackGlobal = useFeatureGate('feature:playback');
  const playbackTier = user?.tier?.features?.playback?.enabled ?? false;
  const showPlayback = playbackGlobal && playbackTier;

  // Load collection on mount
  // Current month/date examples for the search placeholder (YYYY-MM and YYYY-MM-DD)
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const currentDate = `${currentMonth}-${String(now.getDate()).padStart(2, '0')}`;

  useEffect(() => {
    fetchCollection(0, PAGE_SIZE)
      .catch(() => {})
      .finally(() => setInitialLoad(false));
  }, [fetchCollection]);

  // Check if collection feature is disabled at tier level
  if (!collectionEnabled) {
    return (
      <div className="min-h-screen bg-dark-900 text-white">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold mb-4">Collection Not Available</h1>
            <p className="text-gray-400 mb-6">
              Your current tier does not include access to the collection feature.
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-primary hover:bg-primary-dark rounded-lg font-medium transition-colors"
            >
              Back to Dashboard
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Usage ratio for visual indicator
  /* c8 ignore start */
  const usageRatio = collectionMaxClips > 0 ? total / collectionMaxClips : 0;
  const usageColor =
    usageRatio >= 1 ? 'text-red-400' :
    usageRatio >= 0.8 ? 'text-yellow-400' :
    'text-gray-400';
  /* c8 ignore stop */

  const hasMore = clips.length < total;

  const handleLoadMore = () => {
    fetchMore(clips.length, PAGE_SIZE).catch(() => {});
  };

  const handleAddClipFromUrl = async () => {
    const input = window.prompt('Enter Twitch clip URL or clip ID:');
    if (!input) return;

    let clipId = input.trim();
    // If a full URL was provided, try to extract the last path segment as the clip id
    try {
      const u = new URL(clipId);
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts.length > 0) clipId = parts[parts.length - 1];
    } catch (e) {
      // not a URL, assume raw id
    }

    try {
      await addToCollection({ twitch_clip_id: clipId });
      // Success: fetch updated collection to refresh UI
      fetchCollection(0, PAGE_SIZE).catch(() => {});
    } catch (err) {
      // addToCollection sets error in hook; surface a quick alert as well
      /* c8 ignore start */
      alert(err?.message || 'Failed to add clip to collection');
      /* c8 ignore stop */
    }
  };

  const handleRemove = async (clipId) => {
    setConfirmRemoveId(null);
    setRemovingId(clipId);
    try {
      await removeFromCollection(clipId);
    } catch {
      // Error set by hook
    } finally {
      setRemovingId(null);
    }
  };

  const handleCopyLink = async (clipUrl) => {
    if (!clipUrl) return;
    try {
      await navigator.clipboard.writeText(clipUrl);
      setCopiedId(clipUrl);
      // Reset copied state after 2 seconds
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleOpenTagModal = (clip) => {
    setTagModalClip(clip);
    /* c8 ignore start */
    setTagModalTags(clip.tags || []);
    /* c8 ignore stop */
    setTagModalOpen(true);
  };

  const handleOpenBulkTagModal = () => {
    // For bulk mode, pass null for clip and use initial tags from first selected clip
    const firstSelectedClip = clips.find(c => selectedClipIds.has(c.id));
    setTagModalClip(null); // null signals bulk mode
    /* c8 ignore start */
    setTagModalTags(firstSelectedClip?.tags || []);
    /* c8 ignore stop */
    setTagModalOpen(true);
  };

  const handleSaveTags = async (newTags) => {
    setTagSaving(true);
    setTagModalError(null);
    try {
      // If tagModalClip is null, we're in bulk mode
      if (!tagModalClip) {
        // Bulk update mode
        const clipIds = Array.from(selectedClipIds);
        await bulkUpdateTags(clipIds, newTags);
        setSelectedClipIds(new Set()); // Clear selection after bulk update
      } else {
        // Single clip update mode
        await updateClipTags(tagModalClip.id, newTags);
      }
      setTagModalOpen(false);
      setTagModalClip(null);
      setTagModalTags([]);
      setTagModalError(null);
    } catch (err) {
      // Check if this is a denied tag error
      const errorCode = err?.response?.error?.code || err?.message;
      if (errorCode === 'tag_not_allowed' || err?.message?.includes('not allowed')) {
        // Show the error in the modal and keep it open
        /* c8 ignore start */
        setTagModalError(err?.response?.error?.message || err.message || 'One or more tags are not allowed');
        /* c8 ignore stop */
      } else {
        // For other errors, close the modal and show a console error
        console.error('Failed to save tags:', err);
        setTagModalOpen(false);
        setTagModalClip(null);
        setTagModalTags([]);
      }
    } finally {
      setTagSaving(false);
    }
  };

  const handleToggleSelection = (clipId) => {
    setSelectedClipIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(clipId)) {
        newSet.delete(clipId);
      } else {
        newSet.add(clipId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    setSelectedClipIds(new Set(filteredClips.map(c => c.id)));
  };

  const handleDeselectAll = () => {
    setSelectedClipIds(new Set());
  };

  const handleInvertSelection = () => {
    const newSelection = new Set();
    filteredClips.forEach(clip => {
      if (!selectedClipIds.has(clip.id)) {
        newSelection.add(clip.id);
      }
    });
    setSelectedClipIds(newSelection);
  };

  const handleRemoveSelected = async () => {
    /* c8 ignore start */
    if (selectedClipIds.size === 0) return;
    /* c8 ignore stop */
    if (!confirm(`Remove ${selectedClipIds.size} clip${selectedClipIds.size === 1 ? '' : 's'} from collection?`)) return;
    
    const idsToRemove = Array.from(selectedClipIds);
    setSelectedClipIds(new Set()); // Clear selection immediately
    
    // Remove clips sequentially
    for (const clipId of idsToRemove) {
      try {
        await removeFromCollection(clipId);
      } catch (err) {
        console.error('Failed to remove clip:', clipId, err);
      }
    }
  };

  const handleClearSelection = () => {
    setSelectedClipIds(new Set());
  };

  const handlePlayback = () => {
    // Build query params for playback
    let clipsToPlay = [];
    if (selectedClipIds.size > 0) {
      // Play selected clips
      clipsToPlay = filteredClips.filter(c => selectedClipIds.has(c.id));
    /* c8 ignore start */
    } else {
      // Play all filtered clips (or all if no filter)
      clipsToPlay = filteredClips.length > 0 ? filteredClips : clips;
    }
    /* c8 ignore stop */

    if (clipsToPlay.length === 0) {
      alert('No clips to play. Add clips to your collection first.');
      return;
    }

    // Build URL with selected clip IDs
    const selectedIds = clipsToPlay.map(c => c.id).join(',');
    const playbackUrl = `/playback?source=collection&selectedIds=${encodeURIComponent(selectedIds)}`;
    navigate(playbackUrl);
  };

  // Filter clips based on search query (title, creator, game, date, tags)
  const filterClips = (clipsToFilter) => {
    if (!showAdvancedFilters) return clipsToFilter;
    
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return clipsToFilter.filter(clip => {
        // Search by title, creator name, game name, date, and tags (if tagging is enabled)
        /* c8 ignore start */
        const title = String(clip.title || '').toLowerCase();
        const creatorName = String(clip.creatorName || '').toLowerCase();
        const gameName = String(clip.gameName || '').toLowerCase();
        const broadcasterName = String(clip.broadcasterName || clip.broadcasterLogin || '').toLowerCase();
        /* c8 ignore stop */
        
        // Extract DATE portion from ISO timestamp (YYYY-MM-DD)
        const fullTimestamp = String(clip.clipCreatedAt || '');
        const dateOnly = fullTimestamp.split('T')[0].toLowerCase() || '';
        
        // Check if query matches any tag (when tagging is enabled)
        const tagsMatch = showTagging && clip.tags && clip.tags.length > 0
          /* c8 ignore start */
          ? clip.tags.some(tag => String(tag || '').toLowerCase().includes(q))
          /* c8 ignore stop */
          : false;
        
        return (
          title.includes(q) ||
          creatorName.includes(q) ||
          gameName.includes(q) ||
          broadcasterName.includes(q) ||
          dateOnly.includes(q) ||
          tagsMatch
        );
      });
    }
    
    return clipsToFilter;
  };

  const filteredClips = filterClips(clips);
  const isFilterActive = searchQuery.trim();

  return (
    <div className="min-h-screen bg-dark-900 pb-12">
      <Header />

      <main className={`max-w-7xl mx-auto px-4 py-6 ${!initialLoad && !loading && filteredClips.length > 0 ? 'pb-32' : ''}`}>
        {/* Page header with count/limit */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold"><span role="img" aria-label="Collection">📂</span> Collection</h1>
            <p className={`mt-1 ${usageColor}`}>
              <span data-testid="collection-count">{total}</span> / {collectionMaxClips} clips
              {usageRatio >= 1 && ' — Collection full'}
            </p>
          </div>
          
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm" role="alert">
            {error}
          </div>
        )}

        {/* Advanced search filters */}
        {showAdvancedFilters && (
          <div className="mb-6 space-y-3">
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder={`Filter by title, creator, game, or date (e.g., ${currentMonth} or ${currentDate})...`} 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-sm text-white placeholder-gray-400 focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-transparent"
                  data-testid="collection-search-input"
                />
              </div>

              {/* Add Clip button (only when tier does NOT enforce own-channel-only and collection not full) */}
              {!collectionOwnChannelOnly && (
                <div className="flex-none">
                  <button
                    onClick={handleAddClipFromUrl}
                    disabled={total >= collectionMaxClips}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ml-2 ${total >= collectionMaxClips ? 'bg-dark-500 text-gray-500 cursor-not-allowed' : 'bg-primary hover:bg-primary-dark text-white'}`}
                    title={total >= collectionMaxClips ? 'Collection is full' : 'Add clip to collection from a Twitch URL'}
                    data-testid="add-clip-button"
                  >
                    Add Clip
                  </button>
                </div>
              )}
            </div>
            {isFilterActive && (
              <div className="flex items-center justify-between text-sm text-gray-400">
                <span data-testid="filter-results-count">Showing {filteredClips.length} of {clips.length} clips</span>
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-purple-400 hover:text-purple-300 font-medium transition-colors"
                  data-testid="clear-filter-button"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        )}

        {/* Loading state — initial load only */}
        {/* c8 ignore start */}
        {initialLoad && loading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" role="status" aria-label="Loading collection"></div>
          </div>
        )}
        {/* c8 ignore stop */}

        {/* Empty state */}
        {!initialLoad && !loading && filteredClips.length === 0 && clips.length === 0 && !error && (
          <div className="text-center py-20" data-testid="empty-state">
            <div className="text-5xl mb-4" role="img" aria-label="Empty collection">📂</div>
            <h2 className="text-xl font-semibold text-gray-300 mb-2">No clips in your collection yet</h2>
            <p className="text-gray-400 mb-6">
              Go to the Dashboard to browse and add clips to your collection.
            </p>
            <Link
              to="/dashboard"
              className="inline-block px-6 py-2 bg-primary hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
            >
              Go to Dashboard
            </Link>
          </div>
        )}

        {/* No results from filter */}
        {!initialLoad && !loading && filteredClips.length === 0 && clips.length > 0 && (
          <div className="text-center py-20" data-testid="no-filter-results">
            <div className="text-5xl mb-4" role="img" aria-label="No results">🔍</div>
            <h2 className="text-xl font-semibold text-gray-300 mb-2">No clips match your filter</h2>
            <p className="text-gray-400 mb-6">
              Try adjusting your search or clearing the filter to see all clips.
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="inline-block px-6 py-2 bg-primary hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
            >
              Clear Filter
            </button>
          </div>
        )}

        {/* Clip card grid */}
        {filteredClips.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="clip-grid">
            {filteredClips.map((clip) => (
              <div
                key={clip.id}
                className={`bg-dark-800 border rounded-lg overflow-hidden transition-all ${
                  removingId === clip.id ? 'opacity-50' : ''
                } ${
                  selectedClipIds.has(clip.id) ? 'border-primary ring-2 ring-primary/30' : 'border-dark-600'
                }`}
                data-testid="clip-card"
              >
                {/* Thumbnail */}
                <div 
                  className="relative aspect-video bg-dark-700 cursor-pointer group"
                  onClick={() => handleToggleSelection(clip.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    /* c8 ignore start */
                    if (e.key === 'Enter' || e.key === ' ') {
                    /* c8 ignore stop */
                      e.preventDefault();
                      handleToggleSelection(clip.id);
                    }
                  }}
                >
                  {/* Circular selection indicator (always shown) */}
                  <div
                    className={`
                      absolute top-2 left-2 w-6 h-6 rounded-full border-2 z-10
                      flex items-center justify-center transition-all
                      ${selectedClipIds.has(clip.id)
                        ? 'bg-primary border-primary'
                        : 'bg-black/50 border-white/50 group-hover:border-white'
                      }
                    `}
                    data-testid={`select-checkbox-${clip.id}`}
                    aria-hidden="true"
                  >
                    {selectedClipIds.has(clip.id) && (
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  {/* c8 ignore start */}
                  {clip.thumbnailUrl ? (
                    <img
                      src={clip.thumbnailUrl}
                      alt={clip.title || 'Clip thumbnail'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500">
                      <span className="text-3xl" role="img" aria-label="No thumbnail">🎦</span>
                    </div>
                  )}
                  {/* c8 ignore stop */}
                  {/* Duration badge */}
                  {clip.duration != null && (
                    <span className="absolute bottom-1 right-1 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded-sm">
                      {formatDuration(clip.duration)}
                    </span>
                  )}
                </div>

                {/* Card body */}
                <div className="p-3">
                  <h3 className="font-medium text-sm text-white truncate" title={clip.title}>
                    {clip.title || 'Untitled clip'}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-xs text-gray-400">{clip.broadcasterName || 'Unknown channel'}</p>
                    {clip.gameName && (
                      <span className="text-xs bg-purple-900/40 text-purple-300 px-2 py-0.5 rounded-sm">
                        <span aria-hidden="true" className="mr-1">🎮</span>
                        {formatGameName(clip.gameName)}
                      </span>
                    )}
                  </div>
                  {/* Creator and date info */}
                  <div className="mt-2 text-xs text-gray-500">
                    {clip.creatorName && (
                      <p>Clipped by {clip.creatorName}</p>
                    )}
                    {clip.clipCreatedAt && (
                      <p>on {formatDate(clip.clipCreatedAt, 'date')}</p>
                    )}
                  </div>

                  {/* Tag badges */}
                  {showTagging && clip.tags && clip.tags.length > 0 && (
                    <div className="mt-2 flex gap-1 overflow-x-auto no-scrollbar" data-testid={`clip-tags-${clip.id}`}>
                      {clip.tags.map((tag) => (
                        <span
                          key={tag}
                          className="inline-block flex-shrink-0 px-2 py-0.5 bg-purple-900/40 text-purple-300 text-xs rounded-full border border-purple-700/50 whitespace-nowrap"
                          data-testid={`tag-badge-${clip.id}-${tag}`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-end mt-2 gap-1 flex-shrink-0">
                    {/* Edit Tags button */}
                    {/* c8 ignore start */}
                    {showTagging && (
                      <button
                        onClick={() => handleOpenTagModal(clip)}
                        className="text-xs px-2 py-1 bg-dark-600 hover:bg-dark-500 text-gray-300 hover:text-white rounded-sm transition-colors"
                        aria-label={`Edit tags for ${clip.title || 'clip'}`}
                        title="Edit tags"
                        data-testid={`edit-tags-button-${clip.id}`}
                      >
                        🏷️ Tags
                      </button>
                    )}
                    {/* c8 ignore stop */}
                    {/* Copy Link & Remove buttons */}
                    <div className="flex items-center gap-2">
                      {/* Open in new tab */}
                      {/* c8 ignore start */}
                      {clip.url && (
                        <a
                          href={clip.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-xs px-2 py-1 bg-dark-600 hover:bg-dark-500 text-gray-300 hover:text-white rounded-sm transition-colors"
                          aria-label={`Open ${clip.title || 'clip'} in new tab`}
                          title="Open in new tab"
                        >
                          <span aria-hidden="true" className="mr-1">↗</span>
                          <span>Open</span>
                        </a>
                      )}
                      {/* c8 ignore stop */}

                      <button
                        onClick={() => handleCopyLink(clip.url)}
                        className={`text-xs px-2 py-1 rounded transition-colors ${
                          copiedId === clip.url
                            ? 'bg-green-600 text-white'
                            : 'bg-dark-600 hover:bg-dark-500 text-gray-300 hover:text-white'
                        }`}
                        aria-label={`Copy link for ${clip.title || 'clip'}`}
                        title="Copy Twitch link"
                      >
                        {copiedId === clip.url ? '✓ Copied' : '🔗 Copy'}
                      </button>

                      {/* Remove button / Confirm dialog */}
                      {confirmRemoveId === clip.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleRemove(clip.id)}
                            className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded-sm transition-colors"
                            aria-label="Confirm remove"
                            disabled={removingId === clip.id}
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => setConfirmRemoveId(null)}
                            className="text-xs px-2 py-1 bg-dark-600 hover:bg-dark-500 text-gray-300 rounded-sm transition-colors"
                            aria-label="Cancel remove"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmRemoveId(clip.id)}
                          className="text-xs text-gray-500 hover:text-red-400 transition-colors"
                          aria-label={`Remove ${clip.title || 'clip'} from collection`}
                          disabled={removingId === clip.id}
                          title="Remove from collection"
                        >
                          🗑️ Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Load More button */}
        {hasMore && !loading && (
          <div className="text-center mt-6">
            <button
              onClick={handleLoadMore}
              className="px-6 py-2 bg-dark-700 hover:bg-dark-600 text-gray-300 hover:text-white font-medium rounded-lg border border-dark-600 transition-colors"
            >
              Load More
            </button>
          </div>
        )}

        {/* Loading indicator for Load More */}
        {/* c8 ignore start */}
        {loading && !initialLoad && (
          <div className="text-center mt-6 text-gray-400">
            Loading more clips...
          </div>
        )}
        {/* c8 ignore stop */}
      </main>

      {/* Action bar for bulk operations - always visible when clips exist */}
      {!initialLoad && !loading && filteredClips.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50" role="region" aria-label="Clip selection actions" data-testid="bulk-action-bar">
          <div className="max-w-7xl mx-auto px-4 pb-4">
            <div className="bg-dark-700 border border-dark-500 rounded-xl shadow-2xl p-4">
              {/* Selection counter and bulk actions */}
              <div className="flex items-center justify-between gap-4 mb-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/20 text-primary font-bold" aria-hidden="true">
                    {selectedClipIds.size}
                  </div>
                  <div aria-live="polite" aria-atomic="true">
                    <p className="font-medium text-white" data-testid="selected-count">
                      {selectedClipIds.size === 0 ? 'No clips selected' : `${selectedClipIds.size} clip${selectedClipIds.size !== 1 ? 's' : ''} selected`}
                    </p>
                  </div>
                </div>

                {/* Tag editing and Remove buttons */}
                <div className="flex items-center gap-2">
                  {showTagging && (
                    <button
                      onClick={handleOpenBulkTagModal}
                      disabled={selectedClipIds.size === 0}
                      className={`px-6 py-2.5 rounded-lg font-semibold transition-all ${
                        selectedClipIds.size === 0
                          ? 'bg-dark-600 text-gray-500 cursor-not-allowed'
                          : 'text-white bg-primary hover:bg-primary-hover shadow-lg shadow-primary/25'
                      }`}
                      data-testid="bulk-tag-button"
                    >
                      🏷️ Edit Tags
                    </button>
                  )}
                  <button
                    onClick={handleRemoveSelected}
                    disabled={selectedClipIds.size === 0}
                    className={`px-6 py-2.5 rounded-lg font-semibold transition-all ${
                      selectedClipIds.size === 0
                        ? 'bg-dark-600 text-gray-500 cursor-not-allowed'
                        : 'text-white bg-red-600 hover:bg-red-700 shadow-lg shadow-red-600/25'
                    }`}
                    aria-label={`Remove ${selectedClipIds.size} selected clips`}
                    title="Remove selected clips"
                    data-testid="remove-button"
                  >
                    Remove
                  </button>

                  {/* Playback button placed to the right of Remove in the footer bar */}
                  {showPlayback && (
                    <button
                      onClick={handlePlayback}
                      disabled={selectedClipIds.size === 0}
                      className={`px-6 py-2.5 rounded-lg font-semibold transition-all ${
                        selectedClipIds.size === 0
                          ? 'bg-dark-600 text-gray-500 cursor-not-allowed'
                          : 'text-white bg-primary hover:bg-primary-hover shadow-lg shadow-primary/25'
                      }`}
                      aria-label={`Play ${selectedClipIds.size} selected clips`}
                      title={selectedClipIds.size > 0 ? `Play ${selectedClipIds.size} selected clips` : 'Play selected clips'}
                      data-testid="playback-button"
                    >
                      ▶ Playback
                    </button>
                  )}
                </div>
              </div>

              {/* Selection control buttons */}
              <div className="flex items-center gap-2 pt-3 border-t border-dark-500">
                <button
                  onClick={handleSelectAll}
                  className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white hover:bg-dark-600 rounded-sm transition-colors"
                  aria-label="Select all clips"
                  title="Select all clips"
                  data-testid="select-all-button"
                >
                  Select All
                </button>
                <button
                  onClick={handleDeselectAll}
                  className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white hover:bg-dark-600 rounded-sm transition-colors"
                  aria-label="Deselect all clips"
                  title="Deselect all clips"
                  data-testid="deselect-all-button"
                >
                  Deselect All
                </button>
                <button
                  onClick={handleInvertSelection}
                  className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white hover:bg-dark-600 rounded-sm transition-colors"
                  aria-label="Invert selection"
                  title="Invert selection"
                  data-testid="invert-button"
                >
                  Invert
                </button>
                <button
                  onClick={handleClearSelection}
                  className="px-3 py-1.5 text-xs font-medium text-gray-300 hover:text-white hover:bg-dark-600 rounded-sm transition-colors"
                  aria-label={`Clear all ${selectedClipIds.size} selected clips`}
                  title="Clear selection"
                  data-testid="clear-selection-button"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tag Edit Modal */}
      {showTagging && (
        <TagEditModal
          isOpen={tagModalOpen}
          onClose={() => {
            setTagModalOpen(false);
            setTagModalClip(null);
            setTagModalTags([]);
            setTagModalError(null);
          }}
          onSave={handleSaveTags}
          clip={tagModalClip}
          clips={tagModalClip === null ? Array.from(selectedClipIds).map(id => clips.find(c => c.id === id)).filter(Boolean) : null}
          initialTags={tagModalTags}
          isProcessing={tagSaving}
          externalError={tagModalError}
        />
      )}
    </div>
  );
}

